// fin-core / format — money and dates as the screen shows them. Pure, deterministic (no Intl, so no
// ICU difference between a build host and a browser).
//
// Money is INTEGER CENTS everywhere below the screen. This is the only place it becomes text.

const NARROW_NBSP = ' ';

/** `123456` → `1 234,56`: an amount with no currency sign (the books are in euros; the screen does not repeat it
 *  on every figure), a narrow no-break space between thousands and a comma before the cents. */
export function formatAmount(cents: number): string {
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(`formatAmount: ${String(cents)} is not an integer number of cents`);
  }
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const euros = Math.trunc(abs / 100);
  const grouped = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, NARROW_NBSP);
  return `${sign}${grouped},${String(abs % 100).padStart(2, '0')}`;
}

/** `2026-10-05` → `05/10/2026`. Anything that is not an ISO date is returned as it is, never hidden. */
export function formatDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/** `2026-08-01` → `1er août 2026` (the label « depuis l'ouverture du livre »). */
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export function formatDayLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const day = Number(m[3]);
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return iso;
  return `${day === 1 ? '1er' : String(day)} ${month} ${m[1]}`;
}

/** `2026-10-06T05:46:06.006563+00:00` → `07:46` in Paris, or the input if it is not a timestamp. */
export function formatClockParis(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(t));
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '??';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '??';
  return `${hh}:${mm}`;
}
